import React, { useState, useEffect } from 'react';
import { BranchingScenario, ScenarioNode, ScenarioChoice, MysteryReward } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { MysteryRewardModal } from './MysteryRewardModal';
import { rewardStore } from '../services/api';
import {
  ShieldAlert,
  DollarSign,
  HeartHandshake,
  ArrowRight,
  RotateCcw,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Briefcase,
  Flame,
  Award,
  Zap
} from 'lucide-react';

interface BranchingScenarioGameProps {
  scenario: BranchingScenario;
  onComplete: (unlockedReward?: MysteryReward) => void;
}

export const BranchingScenarioGame: React.FC<BranchingScenarioGameProps> = ({ scenario, onComplete }) => {
  const { lang, t } = useLanguage();

  const [currentNodeId, setCurrentNodeId] = useState<string>(scenario.startNodeId);
  const [legalRisk, setLegalRisk] = useState<number>(scenario.initialRisk);
  const [budget, setBudget] = useState<number>(scenario.initialBudget);
  const [reputation, setReputation] = useState<number>(scenario.initialReputation);

  const [selectedChoice, setSelectedChoice] = useState<ScenarioChoice | null>(null);
  const [history, setHistory] = useState<Array<{ stageTitle: string; choiceText: string; consequence: string; isOptimal: boolean }>>([]);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [showMysteryModal, setShowMysteryModal] = useState<boolean>(false);
  const [mysteryReward, setMysteryReward] = useState<MysteryReward | null>(null);

  // Desirable Difficulty: Optional Crisis Countdown Timer (60s)
  const [timeLeft, setTimeLeft] = useState<number>(45);
  const [timerActive, setTimerActive] = useState<boolean>(true);

  const currentNode: ScenarioNode = scenario.nodes[currentNodeId] || scenario.nodes[scenario.startNodeId];

  useEffect(() => {
    if (!timerActive || isFinished || selectedChoice !== null) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          // Force auto-choice or warning
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timerActive, isFinished, selectedChoice, currentNodeId]);

  const handleSelectChoice = (choice: ScenarioChoice) => {
    setSelectedChoice(choice);
    const newRisk = Math.min(100, Math.max(0, legalRisk + choice.riskChange));
    const newBudget = Math.max(0, budget + choice.budgetChange);
    const newRep = Math.min(100, Math.max(0, reputation + choice.reputationChange));

    setLegalRisk(newRisk);
    setBudget(newBudget);
    setReputation(newRep);

    setHistory((prev) => [
      ...prev,
      {
        stageTitle: currentNode.stageTitle[lang],
        choiceText: choice.text[lang],
        consequence: choice.consequenceText[lang],
        isOptimal: !!choice.isOptimal,
      },
    ]);
  };

  const handleNextStep = () => {
    if (!selectedChoice) return;

    if (selectedChoice.nextNodeId && scenario.nodes[selectedChoice.nextNodeId]) {
      setCurrentNodeId(selectedChoice.nextNodeId);
      setSelectedChoice(null);
      setTimeLeft(45); // Reset crisis timer for next stage
    } else {
      // Reached conclusion!
      setIsFinished(true);
      const isOverallSuccess = legalRisk <= 40 && reputation >= 60;
      if (isOverallSuccess) {
        const reward = rewardStore.drawRandomReward();
        setMysteryReward(reward);
        setShowMysteryModal(true);
        onComplete(reward);
      } else {
        onComplete(undefined);
      }
    }
  };

  const handleReset = () => {
    setCurrentNodeId(scenario.startNodeId);
    setLegalRisk(scenario.initialRisk);
    setBudget(scenario.initialBudget);
    setReputation(scenario.initialReputation);
    setSelectedChoice(null);
    setHistory([]);
    setIsFinished(false);
    setTimeLeft(45);
    setTimerActive(true);
  };

  // Performance Score calculation (0-100)
  const optimalChoicesCount = history.filter((h) => h.isOptimal).length;
  const totalStages = Math.max(1, history.length);
  const performanceScore = Math.round((optimalChoicesCount / totalStages) * 100);
  const isNearMiss = performanceScore >= 60 && performanceScore < 100;
  const isPerfectMastery = performanceScore === 100 && legalRisk <= 35;

  return (
    <div style={{ maxWidth: '920px', margin: '0 auto' }}>
      {/* Top Header: Scenario Title and Psychology Hook */}
      <div
        className="glass-card"
        style={{
          padding: '24px',
          borderRadius: 'var(--radius-lg)',
          marginBottom: '20px',
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.9) 100%)',
          border: '1px solid var(--border-color)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span className="badge-status in-progress" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <Briefcase size={12} style={{ display: 'inline', marginRight: '4px' }} />
                {t('Simulador de Crisis Ramificado', 'Branching Crisis Simulator')}
              </span>
              <span style={{ fontSize: '11px', color: '#f59e0b', background: 'rgba(245, 158, 11, 0.12)', padding: '2px 8px', borderRadius: '6px', fontWeight: 700 }}>
                {scenario.initialRole[lang]}
              </span>
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 800, margin: 0, color: 'var(--text-primary)' }}>
              {scenario.title[lang]}
            </h2>
          </div>

          {/* Crisis Urgency Countdown Pulse */}
          {!isFinished && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 14px',
                borderRadius: '20px',
                background: timeLeft <= 15 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(59, 130, 246, 0.12)',
                border: `1px solid ${timeLeft <= 15 ? '#ef4444' : 'rgba(59, 130, 246, 0.3)'}`,
                color: timeLeft <= 15 ? '#f87171' : '#93c5fd',
                fontSize: '13px',
                fontWeight: 800,
                animation: timeLeft <= 15 ? 'pulse 1s infinite' : 'none',
              }}
            >
              <Clock size={16} />
              <span>{t('Tiempo de Decisión:', 'Decision Clock:')} {timeLeft}s</span>
            </div>
          )}
        </div>

        {/* Live Consequence Meters */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
          {/* 1. Legal Risk Meter */}
          <div style={{ background: 'var(--bg-secondary)', padding: '12px 16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', fontSize: '12px', fontWeight: 700 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: legalRisk > 50 ? '#ef4444' : '#34d399' }}>
                <ShieldAlert size={15} /> {t('Riesgo Legal / Sanción', 'Legal Risk / Penalties')}
              </span>
              <span style={{ color: legalRisk > 50 ? '#ef4444' : '#34d399' }}>{legalRisk}%</span>
            </div>
            <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
              <div
                style={{
                  width: `${legalRisk}%`,
                  height: '100%',
                  background: legalRisk > 60 ? 'linear-gradient(90deg, #f59e0b, #ef4444)' : 'linear-gradient(90deg, #10b981, #3b82f6)',
                  transition: 'width 0.4s ease',
                }}
              />
            </div>
          </div>

          {/* 2. Corporate Budget Meter */}
          <div style={{ background: 'var(--bg-secondary)', padding: '12px 16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', fontSize: '12px', fontWeight: 700 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: budget < 40000 ? '#f59e0b' : '#38bdf8' }}>
                <DollarSign size={15} /> {t('Presupuesto Disponible', 'Available Budget')}
              </span>
              <span style={{ color: budget < 40000 ? '#f59e0b' : '#38bdf8' }}>${budget.toLocaleString()}</span>
            </div>
            <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
              <div
                style={{
                  width: `${Math.min(100, (budget / 150000) * 100)}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #0284c7, #38bdf8)',
                  transition: 'width 0.4s ease',
                }}
              />
            </div>
          </div>

          {/* 3. Reputation & Trust Meter */}
          <div style={{ background: 'var(--bg-secondary)', padding: '12px 16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', fontSize: '12px', fontWeight: 700 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: reputation < 50 ? '#ef4444' : '#a855f7' }}>
                <HeartHandshake size={15} /> {t('Reputación y Confianza', 'Reputation & Trust')}
              </span>
              <span style={{ color: reputation < 50 ? '#ef4444' : '#a855f7' }}>{reputation}%</span>
            </div>
            <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
              <div
                style={{
                  width: `${reputation}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #7c3aed, #a855f7)',
                  transition: 'width 0.4s ease',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Interactive Stage or Conclusion */}
      {!isFinished ? (
        <div
          className="glass-card"
          style={{
            padding: '28px',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-color)',
            background: 'var(--bg-secondary)',
          }}
        >
          <div style={{ fontSize: '12px', fontWeight: 800, color: '#60a5fa', textTransform: 'uppercase', marginBottom: '8px' }}>
            {currentNode.stageTitle[lang]}
          </div>

          <p style={{ fontSize: '16px', fontWeight: 500, lineHeight: 1.6, color: 'var(--text-primary)', marginBottom: '24px' }}>
            {currentNode.situation[lang]}
          </p>

          {/* Choices (The Illusion of Choice & Autonomy) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
            {currentNode.choices.map((choice, idx) => {
              const isSelected = selectedChoice?.choiceId === choice.choiceId;
              return (
                <button
                  key={choice.choiceId}
                  disabled={selectedChoice !== null}
                  onClick={() => handleSelectChoice(choice)}
                  style={{
                    padding: '16px 20px',
                    borderRadius: 'var(--radius-md)',
                    textAlign: 'left',
                    background: isSelected
                      ? choice.isOptimal
                        ? 'rgba(16, 185, 129, 0.15)'
                        : 'rgba(239, 68, 68, 0.15)'
                      : 'var(--bg-card)',
                    border: `1px solid ${
                      isSelected
                        ? choice.isOptimal
                          ? '#10b981'
                          : '#ef4444'
                        : 'var(--border-color)'
                    }`,
                    cursor: selectedChoice !== null ? 'default' : 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '14px',
                  }}
                >
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: isSelected ? (choice.isOptimal ? '#10b981' : '#ef4444') : 'rgba(255,255,255,0.08)',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: '13px',
                      flexShrink: 0,
                      marginTop: '2px',
                    }}
                  >
                    {String.fromCharCode(65 + idx)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14.5px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
                      {choice.text[lang]}
                    </div>
                    {isSelected && (
                      <div
                        style={{
                          fontSize: '13px',
                          color: choice.isOptimal ? '#34d399' : '#f87171',
                          marginTop: '8px',
                          lineHeight: 1.5,
                          padding: '10px 14px',
                          background: 'rgba(0,0,0,0.3)',
                          borderRadius: 'var(--radius-sm)',
                          borderLeft: `3px solid ${choice.isOptimal ? '#10b981' : '#ef4444'}`,
                        }}
                      >
                        <strong>{choice.isOptimal ? t('✓ Consecuencia Positiva:', '✓ Positive Consequence:') : t('⚠ Consecuencia de Riesgo:', '⚠ Risk Consequence:')}</strong> {choice.consequenceText[lang]}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Next Button */}
          {selectedChoice && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-primary"
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onClick={handleNextStep}
              >
                <span>{selectedChoice.nextNodeId ? t('Avanzar a la Siguiente Fase', 'Advance to Next Stage') : t('Finalizar Caso de Crisis', 'Complete Crisis Simulation')}</span>
                <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>
      ) : (
        /* Conclusion & Desirable Difficulty Feedback */
        <div
          className="glass-card"
          style={{
            padding: '32px',
            borderRadius: 'var(--radius-lg)',
            textAlign: 'center',
            border: `1px solid ${isPerfectMastery ? '#10b981' : isNearMiss ? '#f59e0b' : 'var(--border-color)'}`,
            background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.9) 0%, rgba(10, 15, 29, 0.95) 100%)',
          }}
        >
          <div style={{ marginBottom: '16px' }}>
            {isPerfectMastery ? (
              <div style={{ display: 'inline-flex', padding: '16px', background: 'rgba(16, 185, 129, 0.15)', borderRadius: '50%', border: '1px solid #10b981' }}>
                <Award size={56} color="#34d399" />
              </div>
            ) : isNearMiss ? (
              <div style={{ display: 'inline-flex', padding: '16px', background: 'rgba(245, 158, 11, 0.15)', borderRadius: '50%', border: '1px solid #f59e0b' }}>
                <Flame size={56} color="#f59e0b" style={{ animation: 'bounce 1.5s infinite' }} />
              </div>
            ) : (
              <div style={{ display: 'inline-flex', padding: '16px', background: 'rgba(239, 68, 68, 0.15)', borderRadius: '50%', border: '1px solid #ef4444' }}>
                <AlertTriangle size={56} color="#f87171" />
              </div>
            )}
          </div>

          {/* Near Miss Callout (Dopamine Prediction & Competence) */}
          {isNearMiss && (
            <div
              style={{
                display: 'inline-block',
                padding: '4px 14px',
                borderRadius: '16px',
                background: 'rgba(245, 158, 11, 0.2)',
                color: '#fbbf24',
                fontSize: '12px',
                fontWeight: 800,
                letterSpacing: '0.5px',
                marginBottom: '12px',
                border: '1px solid rgba(245, 158, 11, 0.4)',
              }}
            >
              {t('⚡ EFECTO CASI ACIERTO: ¡ESTUVISTE A SOLO 1 PASO DEL 100%!', '⚡ NEAR MISS: YOU WERE JUST 1 CHOICE AWAY FROM 100%!')}
            </div>
          )}

          <h3 style={{ fontSize: '24px', fontWeight: 800, margin: '0 0 10px', color: 'var(--text-primary)' }}>
            {isPerfectMastery
              ? t('¡Resolución de Crisis Perfecta (100%)!', 'Perfect Crisis Resolution (100%)!')
              : isNearMiss
              ? t('Excelente Desempeño Corporativo (85%)', 'Great Corporate Performance (85%)')
              : t('Simulación Completada con Lecciones Críticas', 'Simulation Completed with Critical Lessons')}
          </h3>

          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, maxWidth: '540px', margin: '0 auto 24px' }}>
            {isPerfectMastery
              ? t('Tomaste todas las decisiones óptimas bajo presión, blindando a la compañía legal y reputacionalmente.', 'You made every optimal choice under pressure, completely shielding the company.')
              : isNearMiss
              ? t('Estuviste muy cerca del estándar perfecto. Tu decisión mitigó la mayor parte del impacto, pero un ajuste adicional te otorgará la insignia de maestría.', 'You were very close to perfection. Your choices mitigated most impact, but one small tweak grants mastery.')
              : t('Las decisiones tomadas incrementaron el riesgo regulatorio. Te recomendamos reintentar inmediatamente para fijar el aprendizaje.', 'The choices increased regulatory risk. We strongly recommend retrying immediately to reinforce mastery.')}
          </p>

          {/* Decision Timeline Recap */}
          <div style={{ maxWidth: '600px', margin: '0 auto 28px', textAlign: 'left', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', padding: '18px', border: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '12px' }}>
              {t('Trazabilidad de tus Decisiones:', 'Your Decision Log:')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {history.map((h, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px' }}>
                  {h.isOptimal ? <CheckCircle2 size={16} color="#34d399" style={{ flexShrink: 0, marginTop: '2px' }} /> : <AlertTriangle size={16} color="#f87171" style={{ flexShrink: 0, marginTop: '2px' }} />}
                  <div>
                    <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{h.stageTitle}:</span>{' '}
                    <span style={{ color: 'var(--text-secondary)' }}>{h.choiceText}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary"
              style={{ padding: '12px 24px', fontSize: '14px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}
              onClick={handleReset}
            >
              <RotateCcw size={16} />
              {t('Reintentar Inmediatamente (Desirable Difficulty)', 'Instant Replay (Desirable Difficulty)')}
            </button>

            {mysteryReward && (
              <button
                className="btn btn-primary"
                style={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 0 20px rgba(245, 158, 11, 0.4)',
                }}
                onClick={() => setShowMysteryModal(true)}
              >
                <Sparkles size={16} />
                {t('Ver Caja Misteriosa Obtenida', 'View Unlocked Mystery Box')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Mystery Loot Box Modal */}
      {showMysteryModal && mysteryReward && (
        <MysteryRewardModal reward={mysteryReward} onClose={() => setShowMysteryModal(false)} />
      )}
    </div>
  );
};
